import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import clsx from 'clsx';
import {AlbumIcon} from '@ui/icons/material/Album';
import {LiveEvent} from '@app/admin/live-events/interfaces/live-event';
import {
  getMediaImageAspectClass,
  normalizeMediaImageFormat,
} from '@app/web-player/media-image/media-image';
import {normalizeStorageUrl} from '@ui/utils/urls/normalize-storage-url';

interface LiveEventImageProps {
  liveEvent: LiveEvent;
  className?: string;
  size?: string;
  contentFormat?: string;
}
export function LiveEventImage({
  liveEvent,
  className,
  size,
  contentFormat = 'poster',
}: LiveEventImageProps) {
  const {trans} = useTrans();
  const resolvedFormat = normalizeMediaImageFormat(contentFormat) ?? 'poster';
  const src = getLiveEventImageSrc(liveEvent, resolvedFormat);
  const imgClassName = clsx(
    className,
    size,
    'object-cover bg-fg-base/4',
    !src
      ? 'flex items-center justify-center'
      : `${getMediaImageAspectClass(resolvedFormat)} block`,
  );

  return src ? (
    <img
      className={imgClassName}
      draggable={false}
      loading="lazy"
      src={src}
      alt={trans(message('Image for :name', {values: {name: liveEvent.name}}))}
    />
  ) : (
    <span className={clsx(imgClassName, 'overflow-hidden')}>
      <AlbumIcon className="max-w-[60%] text-divider" size="text-9xl" />
    </span>
  );
}

export function getLiveEventImageSrc(
  liveEvent?: LiveEvent,
  contentFormat?: string,
): string | undefined {
  if (!liveEvent) {
    return undefined;
  }

  const requestedFormat = normalizeMediaImageFormat(contentFormat) ?? 'poster';
  const candidates = getCandidates(liveEvent, requestedFormat);

  for (const candidate of candidates) {
    if (candidate) {
      return normalizeStorageUrl(candidate);
    }
  }
}

function getCandidates(
  liveEvent: LiveEvent,
  requestedFormat: 'square' | 'landscape' | 'poster' | 'portrait',
): Array<string | undefined> {
  switch (requestedFormat) {
    case 'landscape':
      return [
        liveEvent.thumb_16_9,
        liveEvent.image,
        liveEvent.thumb_1080,
        liveEvent.artwork,
        liveEvent.thumb_4_5,
        liveEvent.poster,
        liveEvent.thumb_9_16,
      ];
    case 'portrait':
      return [
        liveEvent.thumb_9_16,
        liveEvent.thumb_4_5,
        liveEvent.poster,
        liveEvent.thumb_16_9,
        liveEvent.image,
        liveEvent.thumb_1080,
        liveEvent.artwork,
      ];
    case 'square':
      return [
        liveEvent.thumb_1080,
        liveEvent.image,
        liveEvent.artwork,
        liveEvent.thumb_4_5,
        liveEvent.poster,
        liveEvent.thumb_16_9,
        liveEvent.thumb_9_16,
      ];
    default:
      return [
        liveEvent.thumb_4_5,
        liveEvent.poster,
        liveEvent.thumb_9_16,
        liveEvent.image,
        liveEvent.thumb_1080,
        liveEvent.artwork,
        liveEvent.thumb_16_9,
      ];
  }
}
